'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import Button from '~/components/Button';
import { useRetainedState } from '~/hooks/useRetainedState';
import { cn } from '~/lib/utils';

export const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

export enum Navigation {
  PREVIOUS = 'PREVIOUS',
  NEXT = 'NEXT',
}

export enum Ellipsis {
  START = 'START',
  END = 'END',
}

export type PageType = Navigation | Ellipsis | number;

export interface PaginationProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  total: number;
  itemsPerPage?: number;
  totalItems?: number;
  dense?: boolean;
  className?: string;
  isMinimized?: boolean;
  isLoading?: boolean;
  itemLabel?: string;
  alwaysVisible?: boolean;
  rangeOnly?: boolean;
}

export function getPaginationPages(
  current: number,
  total: number,
  isMinimized?: boolean,
): PageType[] {
  if (isMinimized) {
    return [Navigation.PREVIOUS, current, Navigation.NEXT];
  }

  const maxVisiblePages = 5;

  if (total <= maxVisiblePages) {
    return [
      Navigation.PREVIOUS,
      ...Array.from({ length: total }, (_, i) => i + 1),
      Navigation.NEXT,
    ];
  }

  const halfVisible = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, current - halfVisible);
  const endPage = Math.min(total, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages: PageType[] = [Navigation.PREVIOUS];

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) pages.push(Ellipsis.START);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  if (endPage < total) {
    if (endPage < total - 1) pages.push(Ellipsis.END);
    pages.push(total);
  }

  pages.push(Navigation.NEXT);
  return pages;
}

const formatNumber = (num: number) => num.toLocaleString('en-US');

function RangeLabel({
  startItem,
  endItem,
  totalItems,
  itemLabel,
}: {
  startItem: number;
  endItem: number;
  totalItems: number;
  itemLabel: string;
}) {
  return (
    <p className="text-muted-foreground text-xs">
      <span className="text-foreground font-medium">
        {formatNumber(startItem)}–{formatNumber(endItem)}
      </span>
      <span className="mx-1 opacity-50">of</span>
      <span className="text-foreground/80 font-medium">{formatNumber(totalItems)}</span>
      <span className="ml-1">{itemLabel}</span>
    </p>
  );
}

function NavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: Navigation;
  disabled: boolean;
  onClick: () => void;
}) {
  const isPrev = direction === Navigation.PREVIOUS;
  return (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      aria-label={isPrev ? 'Previous page' : 'Next page'}
      disabled={disabled}
      onClick={onClick}
    >
      {isPrev ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
    </Button>
  );
}

function PageButton({
  page,
  isActive,
  setCurrentPage,
  total: totalPages,
  disabled = false,
}: {
  page: PageType;
  isActive: number;
  setCurrentPage: (page: number) => void;
  total: number;
  disabled?: boolean;
}) {
  if (page === Ellipsis.START || page === Ellipsis.END) {
    return (
      <span className="text-muted-foreground flex size-8 items-center justify-center text-xs">
        …
      </span>
    );
  }

  if (page === Navigation.PREVIOUS || page === Navigation.NEXT) {
    const isPrev = page === Navigation.PREVIOUS;
    const isDisabled = disabled || (isPrev ? isActive === 1 : isActive === totalPages);
    return (
      <NavButton
        direction={page}
        disabled={isDisabled}
        onClick={() =>
          setCurrentPage(isPrev ? Math.max(1, isActive - 1) : Math.min(totalPages, isActive + 1))
        }
      />
    );
  }

  const selected = isActive === page;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      disabled={disabled}
      onClick={() => setCurrentPage(page)}
      aria-label={`Page ${page}`}
      aria-current={selected ? 'page' : undefined}
      className={cn(selected && 'bg-muted text-foreground font-medium')}
    >
      {page}
    </Button>
  );
}

export default function Pagination({
  currentPage,
  setCurrentPage,
  total: totalPages,
  dense: _dense,
  className,
  isMinimized,
  isLoading,
  itemsPerPage,
  totalItems: totalItemsProp,
  itemLabel = 'items',
  rangeOnly = false,
}: PaginationProps) {
  const paginationState = useMemo(() => {
    return [
      {
        totalPages: totalPages,
      },
    ];
  }, [totalPages]);

  const { retainedData } = useRetainedState(paginationState, isLoading || false);

  const displayTotalPages = retainedData?.[0]?.totalPages || 0;

  const visiblePages = useMemo(
    () => getPaginationPages(currentPage, displayTotalPages, isMinimized),
    [currentPage, displayTotalPages, isMinimized],
  );

  const resolvedTotalItems = totalItemsProp ?? 0;
  const startItem = itemsPerPage ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = itemsPerPage ? Math.min(currentPage * itemsPerPage, resolvedTotalItems) : 0;

  if (!totalPages) return null;

  const shellClassName = cn(
    isLoading && 'opacity-70',
    className,
    'border-border/60 mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t pt-3',
  );

  if (rangeOnly && itemsPerPage && resolvedTotalItems > 0) {
    return (
      <div className={shellClassName}>
        <RangeLabel
          startItem={startItem}
          endItem={endItem}
          totalItems={resolvedTotalItems}
          itemLabel={itemLabel}
        />
        <div className="flex items-center gap-1">
          <NavButton
            direction={Navigation.PREVIOUS}
            disabled={!!isLoading || currentPage <= 1}
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          />
          <span className="text-muted-foreground min-w-16 text-center text-xs tabular-nums">
            <span className="text-foreground font-medium">{formatNumber(currentPage)}</span>
            <span className="mx-1 opacity-50">/</span>
            {formatNumber(displayTotalPages)}
          </span>
          <NavButton
            direction={Navigation.NEXT}
            disabled={!!isLoading || currentPage >= displayTotalPages}
            onClick={() => setCurrentPage(Math.min(displayTotalPages, currentPage + 1))}
          />
        </div>
      </div>
    );
  }

  if (totalPages <= 1) return null;

  const range =
    itemsPerPage && resolvedTotalItems > 0 ? (
      <RangeLabel
        startItem={startItem}
        endItem={endItem}
        totalItems={resolvedTotalItems}
        itemLabel={itemLabel}
      />
    ) : (
      <span />
    );

  return (
    <div className={shellClassName}>
      {range}
      <div className="flex items-center gap-0.5">
        {visiblePages.map((p) => (
          <PageButton
            key={`id-${p}`}
            setCurrentPage={setCurrentPage}
            page={p}
            isActive={currentPage}
            total={displayTotalPages}
            disabled={isLoading}
          />
        ))}
      </div>
    </div>
  );
}
